import React, { ReactNode } from 'react';

export type ModalVariant = 'success' | 'error' | 'warning' | 'info';

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message: string | ReactNode;
  icon?: ReactNode;
  variant?: ModalVariant;
  confirmText?: string;
  showCancel?: boolean;
  cancelText?: string;
  onConfirm?: () => void;
}

const variantStyles: Record<ModalVariant, { iconBg: string; button: string }> = {
  success: {
    iconBg: 'bg-green-100 dark:bg-green-900/30',
    button: 'bg-brand-500 text-white hover:bg-brand-600 dark:bg-brand-600 dark:hover:bg-brand-700'
  },
  error: {
    iconBg: 'bg-red-100 dark:bg-red-900/30',
    button: 'bg-red-500 text-white hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700'
  },
  warning: {
    iconBg: 'bg-yellow-100 dark:bg-yellow-900/30',
    button: 'bg-yellow-500 text-white hover:bg-yellow-600 dark:bg-yellow-600 dark:hover:bg-yellow-700'
  },
  info: {
    iconBg: 'bg-blue-100 dark:bg-blue-900/30',
    button: 'bg-blue-500 text-white hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700'
  }
};

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  title,
  message,
  icon,
  variant = 'success',
  confirmText = 'Ok!',
  showCancel = false,
  cancelText = 'Cancel',
  onConfirm
}) => {
  if (!isOpen) return null;

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    } else {
      onClose();
    }
  };

  const styles = variantStyles[variant];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white dark:bg-navy-800 rounded-2xl shadow-2xl p-8 max-w-md w-11/12 mx-4">
        <div className="flex flex-col items-center text-center">

          {icon && (
            <div className={`flex h-20 w-20 items-center justify-center rounded-full ${styles.iconBg} mb-4`}>
              {icon}
            </div>
          )}

          <h3 className="text-2xl font-bold text-navy-700 dark:text-white mb-2">
            {title}
          </h3>

          <div className="text-gray-600 dark:text-gray-400 mb-6">
            {message}
          </div>

          <div className="flex gap-3 w-full">
            {showCancel && (
              <button
                onClick={onClose}
                className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg px-6 py-3 text-sm font-medium transition-all dark:border-gray-500 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                {cancelText}
              </button>
            )}
            <button
              onClick={handleConfirm}
              className={`${showCancel ? 'flex-1' : 'w-full'} ${styles.button} rounded-lg px-6 py-3 text-sm font-medium transition-all shadow-lg hover:shadow-xl`}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;
